/**
 * Shared payment-verification controls — the SINGLE frontend implementation used by
 * BOTH entry points (the Payments Verification queue AND the Payment record page), so
 * there is no duplicated verify logic. Every button calls the ONE backend endpoint
 * (POST /api/accounting/payments/:id/verify) which runs the Financial Orchestrator.
 *
 * Scope (this deployment): Verify + read-only proof/attachment viewing. Reject and the
 * per-type automation (ERN match, mandatory bank reference) are deferred.
 */
import React, { useState, useEffect } from 'react';
import config from '../../config/config';
import { openSharedCardDialog } from './SharedRivhitCardDialog';

const API_BASE = config.api.baseUrl;

export const authHeaders = () => {
  const t = localStorage.getItem('session_token') || sessionStorage.getItem('session_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

// Statuses the server will accept for verify (FE mirrors for enable/disable only —
// the server is authoritative).
const VERIFIABLE = new Set(['Verify', 'In process']);

// Cheque types. For these, "verifying" is not a one-click server action — it IS
// the data entry: accounting counts the physical cheques and enters each one.
// The button therefore delegates to the cheque-entry screen instead of POSTing.
//
// ⚠️ This component stays a thin client. It detects and hands off; it does not
// contain the entry form. The host renders the modal, so there is still exactly
// one implementation of cheque entry.
const CHEQUE_TYPES = new Set(['שיק', 'שיקים']);
const isCheque = (t) => CHEQUE_TYPES.has((t || '').trim());

const C = { green: '#059669', red: '#dc2626', indigo: '#4f46e5', sub: '#64748b', line: '#e2e8f0' };

// Fetch the failure the server persisted on the Payment. Used whenever the verify
// response did not carry a usable reason (e.g. a gateway replaced the body) and to
// get the Rivhit card + names for a name mismatch.
async function fetchIssuanceFailure(paymentId) {
  const r = await fetch(`${API_BASE}/api/accounting/payments/${encodeURIComponent(paymentId)}/issuance-failure`,
    { headers: authHeaders() });
  if (!r.ok) return null;
  return r.json().catch(() => null);
}

const NAME_MISMATCH = 'RIVHIT_NAME_MISMATCH';

/**
 * IssuanceFailurePanel — the detailed reason a verify failed, with the one fix the
 * server offers for it: renaming the Rivhit card to our Account name and verifying.
 * `failure` = {error, reason_code, technical_error, correlation_id, identity}.
 */
function IssuanceFailurePanel({ paymentId, failure, onVerified }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // {ok, text}
  const id = failure?.identity;
  const isMismatch = failure?.reason_code === NAME_MISMATCH && id && id.rivhit_customer_id;
  // 2026-10-25: a card other Accounts share is never renamed — the dialog instead.
  const shared = !!(id && id.rivhit_customer_id && id.shared_count > 0);
  const canRename = isMismatch && !shared && id.rivhit_name && id.account_name && id.names_match === false;
  const openShared = async () => {
    const out = await openSharedCardDialog({ rid: id.rivhit_customer_id, accountId: id.account_id, authHeaders });
    if (out.done) {
      setResult({ ok: true, text: out.currentDetached
        ? 'החשבון נותק מהכרטיס המשותף. אמתו שוב — הקבלה תופק על הכרטיס של הלקוח (או על כרטיס חדש בשמו).'
        : out.approved ? 'החשבונות אושרו כלקוח אחד בהנהלת החשבונות. אמתו שוב.'
        : `נותקו ${out.count} חשבונות מהכרטיס. אמתו שוב.` });
    }
  };
  // 2026-10-23: a DIFFERENT card with this customer's number / ת"ז and name.
  const cand = (isMismatch && id.candidate && id.candidate.customer_id) ? id.candidate : null;
  // An invalid ת"ז (Rivhit -28) is fixed on the Account, not in Rivhit.
  const idProblem = failure?.reason_code === 'RIVHIT_VALIDATION_ERROR' && id && id.account_id;

  const act = async (path, body, okText) => {
    setBusy(true); setResult(null);
    try {
      const r = await fetch(`${API_BASE}/api/accounting/payments/${encodeURIComponent(paymentId)}/${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(body),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        const d = data?.detail || {};
        const done = d.rename?.renamed ? ' (השם ברווחית כבר עודכן)'
          : d.relink?.relinked ? ' (החשבון כבר קושר לכרטיס)'
          : d.written ? ' (רווחית כבר שינתה את הכרטיס — נרשם ביומן)' : '';
        setResult({ ok: false, text: `${d.error || `שגיאה (${r.status})`}${done}` });
      } else {
        const doc = data.document_number ? ` · מסמך ${data.document_number}` : '';
        setResult({ ok: true, text: `${okText}${doc}` });
        if (onVerified) onVerified(data);
      }
    } catch (e) {
      setResult({ ok: false, text: 'שגיאת תקשורת' });
    } finally {
      setBusy(false);
    }
  };

  const renameAndVerify = async () => {
    if (busy) return;
    const ok = window.confirm(
      'לעדכן את שם הלקוח ברווחית?\n\n' +
      `מספר לקוח ברווחית: ${id.rivhit_customer_id}\n` +
      `השם ברווחית כעת: ${id.rivhit_name}\n` +
      `השם החדש (כמו אצלנו): ${id.account_name}\n\n` +
      'לאחר העדכון התשלום יאומת ותופק קבלה.');
    if (!ok) return;
    await act('rivhit-rename-and-verify',
      { rivhit_customer_id: id.rivhit_customer_id, rivhit_name: id.rivhit_name },
      'השם עודכן ברווחית והתשלום אומת');
  };

  const relinkAndVerify = async () => {
    if (busy) return;
    const ok = window.confirm(
      `לקשר את החשבון לכרטיס ${cand.customer_id} ("${cand.name}")?\n\n` +
      `הכרטיס נמצא ברווחית לפי ${cand.matched_by === 'acc_ref' ? 'מספר הלקוח' : 'ת.ז / ח.פ'} של החשבון. ` +
      'רווחית לא משתנה — רק הקישור בחשבון.\n\nלאחר הקישור התשלום יאומת ותופק קבלה על הכרטיס הזה.');
    if (!ok) return;
    await act('rivhit-relink-and-verify',
      { rivhit_customer_id: id.rivhit_customer_id, target_customer_id: cand.customer_id },
      `החשבון קושר לכרטיס ${cand.customer_id} והתשלום אומת`);
  };

  const row = (label, value) => (
    <div style={{ display: 'flex', gap: 6 }}>
      <span style={{ color: C.sub, minWidth: 110 }}>{label}</span>
      <b style={{ wordBreak: 'break-word' }}>{value || '—'}</b>
    </div>
  );

  return (
    <div dir="rtl" style={{
      border: '1px solid #fecaca', background: '#fef2f2', borderRadius: 8, padding: 10,
      fontSize: 12, display: 'flex', flexDirection: 'column', gap: 4, maxWidth: 420, textAlign: 'right',
    }}>
      <span style={{ color: C.red, fontWeight: 700 }}>{failure?.error || 'האימות נכשל'}</span>
      {id && (
        <>
          {row('מספר לקוח ברווחית', id.rivhit_customer_id)}
          {row('השם ברווחית', id.rivhit_name)}
          {row('השם אצלנו', id.account_name)}
          {id.rivhit_id_number && id.rivhit_id_number !== '0' && row('ת.ז/ח.פ ברווחית', id.rivhit_id_number)}
          {id.account_client_id && row('ת.ז/ח.פ אצלנו', id.account_client_id)}
        </>
      )}
      {failure?.identity_error && <span style={{ color: C.sub }}>{failure.identity_error}</span>}
      {(failure?.technical_error || failure?.correlation_id) && (
        <details>
          <summary style={{ cursor: 'pointer', color: C.sub }}>פירוט טכני</summary>
          {failure.technical_error && (
            <div style={{ direction: 'ltr', textAlign: 'left', wordBreak: 'break-all' }}>{failure.technical_error}</div>
          )}
          {failure.correlation_id && <div>מזהה תקלה: {failure.correlation_id}</div>}
        </details>
      )}
      {cand && (
        <>
          {row('כרטיס תואם ברווחית', `${cand.customer_id} · ${cand.name}`)}
          <button onClick={relinkAndVerify} disabled={busy} style={{
            marginTop: 4, padding: '6px 12px', borderRadius: 8, border: 'none', cursor: busy ? 'wait' : 'pointer',
            background: busy ? '#86efac' : '#059669', color: '#fff', fontWeight: 700, fontSize: 12,
          }}>{busy ? 'מקשר ומאמת…' : `קשר לכרטיס ${cand.customer_id} ואמת`}</button>
        </>
      )}
      {shared && (
        <button onClick={openShared} disabled={busy} style={{
          marginTop: 4, padding: '6px 12px', borderRadius: 8, border: 'none', cursor: 'pointer',
          background: '#b45309', color: '#fff', fontWeight: 700, fontSize: 12,
        }}>{`הכרטיס משותף ל-${id.shared_count + 1} חשבונות — בחרו את הלקוח / נתקו`}</button>
      )}
      {canRename && (
        <button onClick={renameAndVerify} disabled={busy} style={{
          marginTop: 4, padding: '6px 12px', borderRadius: 8, border: 'none', cursor: busy ? 'wait' : 'pointer',
          background: busy ? '#fcd34d' : '#d97706', color: '#fff', fontWeight: 700, fontSize: 12,
        }}>{busy ? 'מעדכן ומאמת…' : 'עדכן שם ברווחית ואמת'}</button>
      )}
      {idProblem && (
        <span style={{ color: C.sub }}>
          ת.ז / ח.פ בחשבון אינה תקינה לרווחית ({id.account_client_id || 'ריקה'}). יש לתקן אותה בחשבון ולאמת שוב:{' '}
          <a href={`/object/Account/${id.account_id}`} target="_blank" rel="noreferrer"
            style={{ color: C.indigo, fontWeight: 700 }}>פתח את החשבון</a>
        </span>
      )}
      {result && <span style={{ fontWeight: 600, color: result.ok ? C.green : C.red }}>{result.text}</span>}
    </div>
  );
}

/**
 * VerifyPaymentButton — thin client. No business logic: it POSTs to the one endpoint
 * and reports the server's result. `onDone(result)` lets the host refresh its data.
 * A row that already carries a recorded failure (`issuance_failure_reason`, from the
 * queue) shows it up front, with a "פרטים" link that loads the full reason.
 */
export function VerifyPaymentButton({ payment, onDone, compact, onChequeEntry }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null); // {ok, text}
  const [failure, setFailure] = useState(null); // detailed failure for the panel

  const status = (payment?.payment_status || payment?.Payment_Status__c || '').trim();
  const canVerify = VERIFIABLE.has(status);

  const chequeType = isCheque(payment?.payment_type || payment?.Payment_Type__c);
  const recordedReason = payment?.issuance_failure_reason || '';

  const showDetails = async (fromResponse) => {
    // The response may be missing or partial; the Payment is the source of truth.
    const stored = await fetchIssuanceFailure(payment.id).catch(() => null);
    setFailure({
      ...(stored || {}), ...(fromResponse || {}),
      identity: stored?.identity || null, identity_error: stored?.identity_error,
    });
  };

  const verify = async () => {
    if (busy || !payment?.id) return;
    // A cheque payment is settled by entering its cheques, not by a POST. Posting
    // here would ask the server to issue a receipt for a row that has no cheque
    // details on it at all.
    if (chequeType) {
      if (onChequeEntry) onChequeEntry(payment);
      else setMsg({ ok: false, text: 'יש להזין את פרטי השיקים במסך הזנת שיקים' });
      return;
    }
    setBusy(true); setMsg(null); setFailure(null);
    try {
      const r = await fetch(`${API_BASE}/api/accounting/payments/${encodeURIComponent(payment.id)}/verify`,
        { method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() } });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        // Server rejected (validation / issuance failure / in-doubt). Payment stays as-is.
        const detail = data?.detail;
        const isIssuance = detail && typeof detail === 'object'
          && (detail.reason_code || String(detail.code || '').startsWith('ISSUANCE_'));
        if (typeof detail === 'string') {
          setMsg({ ok: false, text: detail });
        } else if (detail?.error && !isIssuance) {
          // A plain business refusal (already verified, not verifiable…) — one line.
          setMsg({ ok: false, text: detail.error });
        } else {
          // An issuance failure — or a body we could not read at all (a gateway page
          // instead of JSON). Either way, show the full reason recorded on the Payment.
          await showDetails(isIssuance ? detail : null);
        }
      } else {
        const doc = data.document_number ? ` · מסמך ${data.document_number}` : '';
        setMsg({ ok: true, text: `אומת (${data.outcome || 'Success'})${doc}` });
        if (onDone) onDone(data);
      }
    } catch (e) {
      setMsg({ ok: false, text: 'שגיאת תקשורת' });
    } finally {
      setBusy(false);
    }
  };

  if (!canVerify) {
    // Already verified / not a verifiable state — show a neutral marker, no action.
    return <span style={{ color: C.sub, fontSize: 12 }}>{status === 'Success' ? '✓ מאומת' : status}</span>;
  }

  return (
    <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 4, alignItems: 'stretch' }}>
      <button onClick={verify} disabled={busy} style={{
        padding: compact ? '5px 10px' : '8px 16px', borderRadius: 8, cursor: busy ? 'wait' : 'pointer',
        border: 'none', background: busy ? '#a5b4fc' : C.indigo, color: '#fff', fontWeight: 700,
        fontSize: compact ? 12 : 14, whiteSpace: 'nowrap',
      }}>{busy ? 'מאמת…' : 'אמת תשלום'}</button>
      {msg && <span style={{ fontSize: 11, fontWeight: 600, color: msg.ok ? C.green : C.red }}>{msg.text}</span>}
      {!msg && !failure && recordedReason && (
        <span style={{ fontSize: 11, color: C.red }}>
          {recordedReason}{' '}
          <button onClick={() => showDetails(null)} style={{
            border: 'none', background: 'none', color: C.indigo, cursor: 'pointer',
            fontSize: 11, fontWeight: 700, padding: 0, textDecoration: 'underline',
          }}>פרטים</button>
        </span>
      )}
      {failure && (
        <IssuanceFailurePanel paymentId={payment.id} failure={failure}
          onVerified={(data) => {
            setFailure(null);
            setMsg({ ok: true, text: `אומת${data.document_number ? ` · מסמך ${data.document_number}` : ''}` });
            if (onDone) onDone(data);
          }} />
      )}
    </span>
  );
}

/**
 * AttachmentsButton — read-only proof viewer. Opens a lightweight modal listing the
 * record's attachments (images inline + download links). Used for bank-transfer
 * proof (אסמכתה) and any record with attachments.
 */
export function AttachmentsButton({ objectName, recordId, label = 'הצג אסמכתה' }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null);
  const [err, setErr] = useState('');
  const [preview, setPreview] = useState(null); // {src, name} — full-screen image

  const load = async () => {
    setOpen(true); setErr(''); setItems(null);
    try {
      const r = await fetch(`${API_BASE}/api/objects/${objectName}/records/${encodeURIComponent(recordId)}/attachments`,
        { headers: authHeaders() });
      if (!r.ok) throw new Error(`שגיאה (${r.status})`);
      const data = await r.json();
      setItems(Array.isArray(data) ? data : (data.attachments || []));
    } catch (e) { setErr(e.message || 'שגיאה בטעינת אסמכתאות'); }
  };

  const dl = (fileId) =>
    `${API_BASE}/api/objects/${objectName}/records/${encodeURIComponent(recordId)}/attachments/${fileId}/download`;

  return (
    <>
      <button onClick={load} style={{
        padding: '4px 10px', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600,
        border: `1px solid ${C.line}`, background: '#fff', color: C.indigo, whiteSpace: 'nowrap',
      }}>{label}</button>
      {open && (
        <div onClick={() => setOpen(false)} style={{
          position: 'fixed', inset: 0, background: 'rgba(15,23,42,.5)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
        }}>
          <div dir="rtl" onClick={(e) => e.stopPropagation()} style={{
            background: '#fff', borderRadius: 12, padding: 20, maxWidth: 720, maxHeight: '80vh',
            overflow: 'auto', minWidth: 320,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
              <b>אסמכתאות</b>
              <button onClick={() => setOpen(false)} style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 18 }}>✕</button>
            </div>
            {err && <div style={{ color: C.red }}>{err}</div>}
            {!items && !err && <div style={{ color: C.sub }}>טוען…</div>}
            {items && items.length === 0 && <div style={{ color: C.sub }}>אין אסמכתאות לתשלום זה</div>}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
              {(items || []).map((f) => {
                const id = f.id || f.file_id || f.Id;
                const name = f.name || f.filename || f.Name || id;
                const isImg = /\.(png|jpe?g|gif|webp|bmp)$/i.test(name);
                return (
                  <div key={id} style={{ border: `1px solid ${C.line}`, borderRadius: 8, padding: 8, width: 200 }}>
                    {isImg
                      ? <a href={dl(id)} target="_blank" rel="noreferrer"><img src={dl(id)} alt={name}
                          style={{ width: '100%', height: 140, objectFit: 'cover', borderRadius: 6 }} /></a>
                      : <div style={{ height: 140, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.sub }}>📄</div>}
                    <a href={dl(id)} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: C.indigo, wordBreak: 'break-all' }}>{name}</a>
                    {isImg && (
                      <button onClick={() => setPreview({ src: dl(id), name })} style={{
                        display: 'block', width: '100%', marginTop: 6, padding: '5px 8px', borderRadius: 6,
                        border: `1px solid ${C.line}`, background: '#fff', color: C.indigo,
                        cursor: 'pointer', fontSize: 12, fontWeight: 600,
                      }}>תצוגה מקדימה</button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
      {preview && <FullScreenImage src={preview.src} name={preview.name} onClose={() => setPreview(null)} />}
    </>
  );
}

/**
 * FullScreenImage — the proof image at the size of the screen, above the attachments
 * modal. Closes on «סגור», on the backdrop, or on Escape.
 */
function FullScreenImage({ src, name, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(15,23,42,.92)', zIndex: 1100,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }}>
      <img src={src} alt={name} onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: 6 }} />
      <button onClick={onClose} style={{
        position: 'fixed', top: 16, left: 16, padding: '8px 18px', borderRadius: 8, border: 'none',
        background: '#fff', color: '#0f172a', fontWeight: 700, fontSize: 14, cursor: 'pointer',
      }}>✕ סגור</button>
    </div>
  );
}
